/** JWT `typ` header value for Dood anonymous access tokens. */
export const DOOD_ANONYMOUS_JWT_TYP = 'DOOD-ANONYMOUS-JWT';

/**
 * Detect a Dood anonymous JWT from its unprotected header (`typ`).
 * Returns false for Firebase ID tokens or malformed strings.
 */
/** Read JWT header `typ` without validating the signature (debug / detection). */
export function peekJwtHeaderTyp(token: string | null | undefined): string | null {
  if (!token) {
    return null;
  }

  const normalized = token.startsWith('Bearer ') ? token.slice(7) : token;
  const parts = normalized.split('.');
  if (parts.length < 2) {
    return null;
  }

  try {
    const header = JSON.parse(base64UrlDecode(parts[0])) as { typ?: string };
    return header.typ ?? null;
  } catch {
    return null;
  }
}

/** Read JWT payload `sub` without validating the signature (diagnostics only). */
export function peekJwtSubject(token: string | null | undefined): string | null {
  if (!token) {
    return null;
  }

  const normalized = token.startsWith('Bearer ') ? token.slice(7) : token;
  const parts = normalized.split('.');
  if (parts.length < 2) {
    return null;
  }

  try {
    const payload = JSON.parse(base64UrlDecode(parts[1])) as { sub?: unknown };
    return payload.sub == null ? null : String(payload.sub);
  } catch {
    return null;
  }
}

export function isDoodAnonymousJwt(token: string | null | undefined): boolean {
  return peekJwtHeaderTyp(token) === DOOD_ANONYMOUS_JWT_TYP;
}

export function computeTokenExpiresAt(
  expiresInSeconds: number,
  fromMs: number = Date.now(),
): number {
  return fromMs + expiresInSeconds * 1000;
}

export function isTokenExpired(
  expiresAtMs: number | null | undefined,
  nowMs: number = Date.now(),
): boolean {
  if (expiresAtMs == null) {
    return false;
  }
  return nowMs >= expiresAtMs;
}

function base64UrlDecode(value: string): string {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/');
  const padLength = (4 - (padded.length % 4)) % 4;
  const base64 = padded + '='.repeat(padLength);

  if (typeof atob === 'function') {
    return decodeURIComponent(
      Array.prototype.map
        .call(atob(base64), (c: string) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    );
  }

  // Node / SSR fallback
  return Buffer.from(base64, 'base64').toString('utf-8');
}
